import React from 'react';
import { useTranslation } from 'react-i18next';
import { X, ClipboardList, Star, SkipForward, Clock, Loader2 } from 'lucide-react';

const StarRating = ({ score, maxScore = 5 }) => {
    const stars = [];
    for (let i = 1; i <= maxScore; i++) {
        stars.push(
            <Star
                key={i}
                size={16}
                className={i <= score ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
            />
        );
    }
    return <div className="flex items-center gap-0.5">{stars}</div>;
};

export default function FeedbackModal({ isOpen, onClose, feedbackData, loading, error, candidateInfo }) {
    const { t } = useTranslation();

    if (!isOpen) return null;

    const candidateName = candidateInfo
        ? `${candidateInfo.first_name || ''} ${candidateInfo.last_name || ''}`.trim()
        : '';

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden animate-in fade-in zoom-in duration-200"
                style={{ maxHeight: '85vh' }}
            >
                {/* Header */}
                <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="bg-white/20 p-2 rounded-lg">
                            <ClipboardList className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-white">
                                {t('feedback.modalTitle', 'Post-Interview Feedback')}
                            </h2>
                            {candidateName && (
                                <p className="text-emerald-100 text-xs">{candidateName}</p>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-white/80 hover:text-white hover:bg-white/20 rounded-full p-1.5 transition-colors"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 80px)' }}>
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                            <Loader2 className="w-8 h-8 animate-spin text-emerald-500 mb-3" />
                            <p className="text-sm">{t('feedback.loading', 'Loading feedback...')}</p>
                        </div>
                    ) : error ? (
                        <div className="text-center py-12">
                            <div className="w-12 h-12 mx-auto mb-3 bg-red-50 rounded-full flex items-center justify-center">
                                <X className="w-6 h-6 text-red-400" />
                            </div>
                            <p className="text-sm text-red-600">{error}</p>
                        </div>
                    ) : !feedbackData ? (
                        <div className="text-center py-12">
                            <div className="w-16 h-16 mx-auto mb-4 bg-gray-50 rounded-full flex items-center justify-center">
                                <ClipboardList className="w-8 h-8 text-gray-300" />
                            </div>
                            <p className="text-gray-500 text-sm font-medium">
                                {t('feedback.noFeedback', 'No feedback submitted for this interview.')}
                            </p>
                            <p className="text-gray-400 text-xs mt-1">
                                {t('feedback.noFeedbackHint', 'The candidate did not submit post-interview feedback.')}
                            </p>
                        </div>
                    ) : feedbackData.is_skipped ? (
                        <div className="text-center py-10">
                            <div className="w-14 h-14 mx-auto mb-4 bg-amber-50 rounded-full flex items-center justify-center">
                                <SkipForward className="w-7 h-7 text-amber-400" />
                            </div>
                            <p className="text-gray-700 text-sm font-semibold">
                                {t('feedback.skipped', 'Candidate Skipped Feedback')}
                            </p>
                            <p className="text-gray-400 text-xs mt-1">
                                {t('feedback.skippedHint', 'The candidate chose to skip the optional questionnaire.')}
                            </p>
                            {feedbackData.submitted_at && (
                                <div className="mt-4 inline-flex items-center gap-1.5 text-xs text-gray-400 bg-gray-50 px-3 py-1.5 rounded-full">
                                    <Clock size={12} />
                                    {new Date(feedbackData.submitted_at).toLocaleString('en-US', {
                                        day: 'numeric', month: 'short', year: 'numeric',
                                        hour: '2-digit', minute: '2-digit', hour12: true
                                    })}
                                </div>
                            )}
                        </div>
                    ) : (
                        <>
                            {/* Questionnaire title */}
                            {feedbackData.questionnaire?.title && (
                                <div className="mb-4">
                                    <h3 className="text-sm font-bold text-gray-800">
                                        {feedbackData.questionnaire.title}
                                    </h3>
                                    {feedbackData.questionnaire.description && (
                                        <p className="text-xs text-gray-500 mt-0.5">
                                            {feedbackData.questionnaire.description}
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Questions & Answers */}
                            <div className="space-y-4">
                                {(feedbackData.questionnaire?.questions || []).map((q) => {
                                    const answer = feedbackData.responses?.[q.id];
                                    return (
                                        <div
                                            key={q.id}
                                            className="border border-gray-100 rounded-xl p-4 bg-gray-50/50 hover:bg-gray-50 transition-colors"
                                        >
                                            <h4 className="text-sm font-semibold text-gray-800 mb-2">
                                                {q.question_text}
                                            </h4>
                                            <div className="text-sm text-gray-700">
                                                {q.question_type === 'rating' ? (
                                                    <div className="flex items-center gap-2">
                                                        <StarRating score={answer ? parseInt(answer) : 0} maxScore={5} />
                                                        <span className="font-bold text-emerald-700 text-xs bg-emerald-50 px-2 py-0.5 rounded-full">
                                                            {answer || 0}/5
                                                        </span>
                                                    </div>
                                                ) : q.question_type === 'single_choice' || q.question_type === 'multiple_choice' ? (
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {(Array.isArray(answer) ? answer : [answer]).filter(Boolean).map((opt, i) => (
                                                            <span
                                                                key={i}
                                                                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-100"
                                                            >
                                                                {opt}
                                                            </span>
                                                        ))}
                                                        {(!answer || (Array.isArray(answer) && answer.length === 0)) && (
                                                            <span className="text-gray-400 italic text-xs">
                                                                {t('feedback.noAnswer', 'No answer provided')}
                                                            </span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="bg-white border border-gray-100 rounded-lg p-3 text-sm text-gray-700">
                                                        {answer || (
                                                            <span className="text-gray-400 italic">
                                                                {t('feedback.noAnswer', 'No answer provided')}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Submitted timestamp */}
                            {feedbackData.submitted_at && (
                                <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400">
                                    <span>{t('feedback.submittedAt', 'Submitted At')}</span>
                                    <span className="font-semibold text-gray-600 flex items-center gap-1.5">
                                        <Clock size={12} />
                                        {new Date(feedbackData.submitted_at).toLocaleString('en-US', {
                                            day: 'numeric', month: 'short', year: 'numeric',
                                            hour: '2-digit', minute: '2-digit', hour12: true
                                        })}
                                    </span>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
